"use client";

import React from "react";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { IFooter } from "@/sanity/lib/definitions";
import {
  resolveI18nText,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";

import logoFullImg from "@/public/lp2/global/logo-full.svg";
import communityImg from "@/public/lp2/footer/community.svg";
import yunicornLogo from "@/public/global/yunicorn_logo.svg";

export default function Footer({
  data,
  className,
}: {
  data: IFooter;
  className?: string;
}) {
  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;
  if (!data) return null;

  return (
    <footer className={cn(className, "w-full relative z-10")}>
      <div className="container-lp mx-auto py-[4rem]">
        <div className="flex flex-col lg:flex-row items-center justify-start gap-[3rem] lg:gap-0">
          <Image
            className="h-[3.625rem] w-[16.5rem]"
            alt="Logo"
            src={logoFullImg}
          />
          <div className="flex gap-[1rem] items-center ms-[3rem]">
            <span className="text-white text-[2rem] font-[400] opacity-60">
              {resolveI18nText(data?.communityTitle, data?.communityTitleI18n, language)}
            </span>

            <div className="flex justify-center items-center gap-[.5rem] w-[9.5rem] h-[4.5rem] min-w-[9.5rem] min-h-[4.5rem] bg-white/10 rounded-[1.5rem] px-[1rem] border border-white/10">
              <Image
                className="h-[4.5rem]"
                alt="Community"
                src={communityImg}
              />
              <span className="text-white text-[2rem] font-[400] opacity-60">
                50k
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center lg:justify-end gap-[4rem] basis-1/2 lg:ms-auto">
            {data?.impressumLink && (
              <Link
                className="text-white text-[2rem] opacity-70 font-[400] hover:opacity-90 transition-opacity hover:underline"
                href={resolvePageLink(data?.impressumLink, language) ?? "/"}
                target={data.impressumLink?.openInNewTab ? "_blank" : "_self"}
              >
                <span>{resolveI18nText(data?.impressumTitle, data?.impressumTitleI18n, language)}</span>
              </Link>
            )}

            {data?.datenschutzLink && (
              <Link
                className="text-white text-[2rem] opacity-70 font-[400] hover:opacity-90 transition-opacity hover:underline"
                href={resolvePageLink(data?.datenschutzLink, language) ?? "/"}
                target={data.datenschutzLink?.openInNewTab ? "_blank" : "_self"}
              >
                <span>{resolveI18nText(data?.datenschutzTitle, data?.datenschutzTitleI18n, language)}</span>
              </Link>
            )}
          </div>
        </div>

        <hr className="h-[0.125rem] mt-[2.5rem] mb-[3rem] border-white opacity-20" />

        <div className="flex flex-col lg:flex-row items-center justify-between gap-[2rem] lg:gap-0">
          <span className="text-[2rem] text-white opacity-70 font-[400]">
            © {new Date().getFullYear()} Trademania. All rights reserved.
          </span>

          <div className="flex items-center justify-center gap-[0.25rem]">
            <span className="text-[2rem] text-white opacity-70 font-[400]">
              Designed by
            </span>
            <a href="https://yunicorn.vc/" target="_blank" className="">
              <Image
                src={yunicornLogo}
                alt="Yunicorn Logo"
                className="inline h-[1.8rem] w-[9rem] mb-[0.4rem]"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
